import React, {useEffect, useRef, useState} from "react";
import {Box, InputAdornment, Stack, ToggleButton, ToggleButtonGroup, Typography} from "@mui/material";
import {Lock as LockIcon} from "@mui/icons-material";
import {completude} from "../sistemaPagina";
import CampoAutoSalvo from "./CampoAutoSalvo";
import SecaoCard from "./SecaoCard";
import {CompletudeChip, SistemaLogo, StatusChip} from "./SistemaVisual";

const SIGLA_REGEX = /^[A-Z0-9]{1,3}$/;

export const validarSigla = (valor) => (SIGLA_REGEX.test(valor) ? '' : 'A sigla tem de 1 a 3 letras sem acento ou números.');

const validarPortal = (valor) => (!valor || /^https:\/\/\S+$/.test(valor) ? '' : 'O portal começa com https://.');

export const URL_PRIMEIRO_ACESSO_MAX = 500;

export const validarUrlPrimeiroAcesso = (valor) => {
    if (!valor) return '';
    if (!/^https:\/\/\S+$/.test(valor)) return 'O link começa com https:// e não tem espaços.';
    return valor.length > URL_PRIMEIRO_ACESSO_MAX ? `O link passa de ${URL_PRIMEIRO_ACESSO_MAX} caracteres.` : '';
};

const Rotulo = ({children}) => (
    <Typography variant="caption" color="text.secondary" component="div" sx={{mb: 0.5}}>{children}</Typography>
);

// A cor grava um instante depois de a pessoa parar de escolher: o seletor nativo não avisa quando fecha.
const CorDaMarca = ({sistema, onSalvar, onErro}) => {
    const [cor, setCor] = useState((sistema.cor || '#475569').toLowerCase());
    const salvaRef = useRef({onSalvar, onErro});
    salvaRef.current = {onSalvar, onErro};

    useEffect(() => {
        setCor((sistema.cor || '#475569').toLowerCase());
    }, [sistema.cor]);

    useEffect(() => {
        if (cor === (sistema.cor || '').toLowerCase()) return undefined;
        const timer = window.setTimeout(() => salvaRef.current.onSalvar(cor).catch((error) => {
            setCor((sistema.cor || '#475569').toLowerCase());
            salvaRef.current.onErro(error);
        }), 600);
        return () => window.clearTimeout(timer);
    }, [cor, sistema.cor]);

    return (
        <Box>
            <Rotulo>Cor da marca</Rotulo>
            <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                    component="input"
                    type="color"
                    value={cor}
                    onChange={(event) => setCor(event.target.value)}
                    aria-label="Cor da marca"
                    sx={{width: 44, height: 36, p: 0.25, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'transparent', cursor: 'pointer'}}
                />
                <SistemaLogo sistema={{...sistema, cor}} tamanho={32} />
                <Typography variant="caption" color="text.secondary">Vai para o checkout e para a escolha de assinatura.</Typography>
            </Stack>
        </Box>
    );
};

const StatusDoSistema = ({sistema, onStatus}) => {
    if (sistema.status === 'VENDA') {
        return (
            <Box>
                <Rotulo>Status</Rotulo>
                <StatusChip status="VENDA" />
                <Typography variant="caption" color="text.secondary" component="div" sx={{mt: 0.5}}>
                    À venda: os planos do sistema podem ser publicados, e o status não volta atrás.
                </Typography>
            </Box>
        );
    }
    return (
        <Box>
            <Rotulo>Status</Rotulo>
            <ToggleButtonGroup
                exclusive
                size="small"
                value={sistema.status}
                onChange={(_, valor) => valor && valor !== sistema.status && onStatus(valor)}
                aria-label="Status do sistema"
                sx={{'& .MuiToggleButton-root': {textTransform: 'none'}}}
            >
                <ToggleButton value="DESENVOLVIMENTO">Em desenvolvimento</ToggleButton>
                <ToggleButton value="INTERNO">Uso interno</ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" component="div" sx={{mt: 0.5}}>
                {sistema.status === 'INTERNO'
                    ? 'Uso interno: o sistema não entra em planos de preço nem vai à venda.'
                    : 'Para vender, use "Pôr à venda" no topo da página.'}
            </Typography>
        </Box>
    );
};

export default function DadosSecao({sistema, pendencias, onSalvar, onErro}) {
    const salvar = (campo, rotulo) => (valor) => onSalvar({[campo]: valor}, rotulo);
    return (
        <SecaoCard id="dados" titulo="Dados do sistema" chip={<CompletudeChip completude={completude('dados', {sistema, pendencias})} />}>
            <Box sx={{display: 'grid', gap: 2.5, gridTemplateColumns: {xs: '1fr', md: '1fr 160px'}}}>
                <CampoAutoSalvo
                    label="Nome"
                    value={sistema.nome}
                    required
                    validar={(valor) => (valor ? '' : 'O nome não pode ficar vazio.')}
                    onSalvar={salvar('nome', 'nome')}
                />
                <CampoAutoSalvo
                    label="Sigla"
                    value={sistema.sigla}
                    required
                    inputProps={{maxLength: 3, style: {textTransform: 'uppercase'}}}
                    validar={(valor) => validarSigla(valor.toUpperCase())}
                    onSalvar={(valor) => onSalvar({sigla: valor.toUpperCase()}, 'sigla')}
                />
                <Box sx={{gridColumn: {md: '1 / -1'}}}>
                    <CampoAutoSalvo
                        label="Descrição"
                        value={sistema.descricao}
                        multiline
                        minRows={2}
                        onSalvar={salvar('descricao', 'descrição')}
                    />
                </Box>
                <CampoAutoSalvo
                    label="Portal"
                    value={sistema.portal}
                    placeholder="https://"
                    validar={validarPortal}
                    onSalvar={(valor) => onSalvar({portal: valor || null}, 'portal')}
                />
                <Box sx={{display: {xs: 'none', md: 'block'}}} />
                <CampoAutoSalvo
                    label="Link do primeiro acesso"
                    value={sistema.url_primeiro_acesso}
                    placeholder="https://"
                    inputProps={{maxLength: URL_PRIMEIRO_ACESSO_MAX}}
                    validar={validarUrlPrimeiroAcesso}
                    helperText="Vai no e-mail em que o primeiro administrador de cada cliente cria a senha."
                    onSalvar={(valor) => onSalvar({url_primeiro_acesso: valor || null}, 'link do primeiro acesso')}
                />
                <Box sx={{display: {xs: 'none', md: 'block'}}} />
                <Box sx={{gridColumn: {md: '1 / -1'}}}>
                    <CorDaMarca sistema={sistema} onSalvar={salvar('cor', 'cor da marca')} onErro={onErro} />
                </Box>
                <CampoAutoSalvo
                    label="Chave"
                    value={sistema.chave}
                    disabled
                    mono
                    onSalvar={() => Promise.resolve()}
                    InputProps={{startAdornment: <InputAdornment position="start"><LockIcon fontSize="small" /></InputAdornment>}}
                    helperText="Não muda depois de criada: permissões, integrações e dados de uso apontam para ela."
                />
                <Box sx={{display: {xs: 'none', md: 'block'}}} />
                <Box sx={{gridColumn: {md: '1 / -1'}}}>
                    <StatusDoSistema sistema={sistema} onStatus={(status) => onSalvar({status}, 'status').catch(onErro)} />
                </Box>
            </Box>
        </SecaoCard>
    );
}
